import { ref, watch } from 'vue'

import type {
  PageCMSError,
  SupportBanner,
} from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import { useStaffPermissions } from '@backmarket/nuxt-layer-oauth/useStaffPermissions'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { usePageParamsFromRoute } from './usePageParamsFromRoute'

type NotNull<T> = Exclude<T, null | undefined>

// checks if the prop exists and is not null/undefined in an object
// if it's the case, the object is narrowed to the type with the prop required
const hasNotNullProp = <T extends object, Key extends keyof T>(
  obj: T,
  prop: Key,
): obj is T & Required<Record<Key, NotNull<T[Key]>>> => {
  return !!obj && typeof obj === 'object' && prop in obj && !!obj[prop]
}

export function useFetchPageSupportBanner(
  blockErrors: PageCMSError[] | undefined,
) {
  const { canAccessCmsContent } = useStaffPermissions()
  const pathParams = usePageParamsFromRoute()
  const $httpFetch = useHttpFetch()
  const logger = useLogger()

  const supportBanner = ref<SupportBanner>()

  // only create watcher on client to prevent SSR memory leaks
  if (import.meta.client) {
    watch(
      canAccessCmsContent,
      async () => {
        if (!canAccessCmsContent.value) return

        let response
        try {
          response = hasNotNullProp(pathParams, 'pageCategory')
            ? await $httpFetch(
                '/content/admin/page/:pageType/:pageName/:pageCategory',
                { pathParams },
              )
            : await $httpFetch('/content/admin/page/:pageType/:pageName', {
                pathParams,
              })
        } catch (error) {
          logger.error('[CMS SUPPORT BANNER FETCH FAILED]', {
            error: error as Error,
            owners: ['bot-squad-content-front'],
          })
        }

        const filteredErrors =
          response?.errors?.filter(
            (error) => !error.message.match(/AttributeError/i),
          ) ?? []

        const combinedErrors = [...filteredErrors, ...(blockErrors ?? [])]

        supportBanner.value = { ...response, errors: combinedErrors }
      },
      { immediate: true },
    )
  }

  return supportBanner
}
